import { PageHeader } from "~/components/PageHeader";
import { SectionHeader } from "~/components/SectionHeader";
import { QueryResponse } from "~/modules/filters/types";
import { VisualizationGrid } from "~/modules/visualization/components/VisualizationGrid";
import { NetFlowsData } from "~/modules/visualization/util/queryNetFlows";
import { ManagerTable } from "../../components/ManagerTable";
import { ManagerWithAggregates } from "../..";
import { LeagueTablePieChart } from "~/modules/league_tables/components/LeagueTablePieChart";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";
import { DownloadButton } from "~/modules/downloads/components/DownloadButton";
import { ExportTableNames } from "~/modules/downloads/types";
import { DEFAULT_MANAGER_LIST_QUERY } from "../../consts";
import { MetricTimeseriesChart } from "~/modules/visualization/components/MetricTimeseriesChart";
import { TokenTimeseriesGroupBy } from "~/modules/token_timeseries/types";
import {
    Props as AggregateDisplayProps,
    AggregateGrid,
} from "~/components/AggregateDisplay";
import { NetFlowsCard } from "~/modules/visualization/components/NetFlowsChart/NetFlowsCard";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";

// // // //

/**
 * ManagerListPage
 * Main page component for Asset Managers listing with timeseries visualization
 */
export function ManagerListPage(props: {
    aggregates: Array<AggregateDisplayProps>;
    listQueryResponse: QueryResponse<ManagerWithAggregates>;
    netFlows: NetFlowsData;
    leagueTableTabs: LeagueTableTabsByDistributionType;
}) {
    const { aggregates, listQueryResponse, netFlows, leagueTableTabs } = props;

    return (
        <div className="flex flex-col h-full grow">
            <PageHeader
                title="Asset Managers"
                subtitle={
                    <>
                        Analyze operational performance and the competitive
                        landscape of asset managers overseeing tokenized asset
                        portfolios. Note that not all RWAs have a manager (eg.
                        individual stocks or bonds), and this page does not
                        include stablecoins.
                    </>
                }
            />

            <AggregateGrid aggregates={aggregates} />

            <VisualizationGrid className="mt-6">
                <MetricTimeseriesChart
                    title="Asset Manager Metrics"
                    analyticsChartName="asset-manager-metrics"
                    columnSpanConfig={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                        xxl: 12,
                    }}
                    controls={{
                        groupBy: {
                            enabled: false,
                            value: TokenTimeseriesGroupBy.manager,
                        },
                        distributionSelect: {
                            distributed: {
                                enabled: true,
                            },
                            represented: {
                                enabled: true,
                            },
                            stablecoins: {
                                enabled: false,
                                active: false,
                            },
                        },
                    }}
                />

                <LeagueTablePieChart
                    title="Total RWA Value"
                    leagueTableTabs={leagueTableTabs}
                    defaultGroupBy={LeagueTableGroupTypes.managers}
                    colSpans={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 6,
                        xxl: 6,
                    }}
                />

                <NetFlowsCard netFlows={netFlows} />
            </VisualizationGrid>

            <div className="mt-8">
                <SectionHeader
                    label="Asset Managers"
                    value={`${listQueryResponse.results.length.toLocaleString()} Total`}
                >
                    <div className="flex justify-end items-center">
                        <DataExportProvider>
                            {({ isLoading, exportCsv }) => (
                                <DownloadButton
                                    disabled={isLoading}
                                    onClick={() => {
                                        exportCsv({
                                            exportTableName:
                                                ExportTableNames.managers_v4,
                                            query: DEFAULT_MANAGER_LIST_QUERY,
                                        });
                                    }}
                                />
                            )}
                        </DataExportProvider>
                    </div>
                </SectionHeader>

                <ManagerTable rows={listQueryResponse.results} />
            </div>
        </div>
    );
}
